import React from 'react';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import { useAppSelector, useMetricFormatter, useWeekRange, WeekRange } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { useDorisData } from 'src/utils/Hooks/useDorisData';
import useDorisMetricByWeekId from 'src/serverProxy/useDorisMetricByWeekId';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';
import { safeDivide } from 'src/utils/app';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Grid } from '@mui/material';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { getFirstWeekIdOfYear, yearPartOfWeekId } from 'src/utils/dateUtils';

/**
 * Displays visualizations for the content score page
 */
const AtlasV2ContentV2TopSection = () => {
  const formatMetric = useMetricFormatter();
  const weekRange = useWeekRange();
  const isLastWeek = weekRange === WeekRange.LAST_WEEK;
  const { endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const { data: dorisRetailSalesData, isLoading: dorisRetailSalesDataLoading } = useDorisData({
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    metricType: DorisMetricTypeEnum.RetailSales,
    fieldName: DorisGroupByFieldNameEnum.Retailer,
    ...(isLastWeek ? { customTimePeriods: { startWeekId: endWeek, endWeekId: endWeek } } : {})
  });

  const {
    primaryTotal,
    lastPrimaryValue,
    isLoading: isLoadingPromotionSales
  } = useDorisMetricByWeekId({
    queryKey: 'promotion-sales-weekly',
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    metricType: DorisMetricTypeEnum.PromotionSales,
    resultCount: 1200
  });

  const percentage = safeDivide(
    isLastWeek ? lastPrimaryValue : primaryTotal,
    dorisRetailSalesData?.records?.[0]?.value
  );

  // The inner spline chart owns the CSV export logic (derived from the exact series it
  // renders), but its own menu is hidden. We capture that handler here and expose it on
  // the outer wrapper's three-dot menu so "Export CSV" downloads data matching the UI.
  const exportPromotionCountCsvRef = React.useRef<(() => Promise<unknown>) | undefined>(undefined);

  const handleExportPromotionCountCsv = async () => {
    if (exportPromotionCountCsvRef.current) {
      await exportPromotionCountCsvRef.current();
    }
  };

  return (
    <BeaconChartWithLegend
      title="Promotion Count"
      includeSummaryInfo
      convertSeriesToDelimitedData={handleExportPromotionCountCsv}
    >
      <Grid display="grid" gridTemplateColumns="308px 655px" justifyContent="space-between" alignItems="center">
        <div style={{ width: '308px', height: '308px' }}>
          {dorisRetailSalesDataLoading || isLoadingPromotionSales ? (
            <DonutChartLoading />
          ) : (
            <CircularProgressPercentage
              percentage={percentage}
              customLabel={
                <Flex gap="sm" alignItems="center" flexDirection="column">
                  <Text
                    sx={{
                      fontSize: '48px',
                      fontWeight: 'bold'
                    }}
                  >
                    {`${formatMetric(percentage, METRICTYPE.PERCENT, {})}`}
                  </Text>
                  <Text
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Share of Total Sales
                  </Text>
                </Flex>
              }
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <AtlasV2DorisSplineChart
            queryKey="promotion-count-weekly"
            title="Promotion Count"
            methodName={DorisMetricEndPointEnum.SalesEndPoint}
            metricTypeDisplay={METRICTYPE.VOLUME}
            metricType={DorisMetricTypeEnum.PromotionCount}
            showTitleSummary={false}
            beaconChartWithLegendOverride={(props) => {
              exportPromotionCountCsvRef.current = props.convertSeriesToDelimitedData;
              return {
                ...props,
                showExportMenu: false,
                loadingComponent: <SplineChartLoading width={664} />
              };
            }}
            chartOptionsOverride={{
              chart: {
                height: 280,
                width: 664
              }
            }}
          />
        </div>
      </Grid>
    </BeaconChartWithLegend>
  );
};

export default AtlasV2ContentV2TopSection;
